import type { EnvironmentThreadShell } from "@cleo/client-runtime/state/shell";
import { squashAtomCommandFailure } from "@cleo/client-runtime/state/runtime";
import type { EnvironmentId } from "@cleo/contracts";
import { useCallback } from "react";

import type { RoomRailGroups, RoomRailSessionNode } from "~/neucleos/projectRoomRailGroups";
import type { RoomRailPreferences } from "~/neucleos/roomRailArrangement";
import { appAtomRegistry } from "~/rpc/atomRegistry";
import { environmentServerConfigsAtom } from "~/state/server";
import { threadEnvironment } from "~/state/threads";
import { useAtomCommand } from "~/state/use-atom-command";
import { mergeActiveOrder } from "./RoomRailSection.logic";
import { toastManager } from "./ui/toast";

/**
 * UP13 — the persisted manual session order (upstream #9729 re-created).
 * The World keeps one fractional `activeOrderKey` per thread, written through
 * `thread.active.reorder` with the pinned block's key math; settling clears
 * the key, pinning keeps it. The rail reads keys first and falls back to the
 * device's manual order for keyless threads, so a refresh, another desk and
 * Pocket all show one arrangement.
 *
 * The command, its `thread.meta-updated` carriage and the shell field are the
 * RUNTIME lane's (b3dd732f); the gate below is the `threadActiveReorder`
 * capability, so a server that predates the command keeps the device-local
 * order and nothing here errors under version skew.
 */

/** The runtime's reorder door: one thread, one key (the pinned block's shape). */
export const activeReorderDoor: typeof threadEnvironment.reorderActive | null =
  threadEnvironment.reorderActive ?? null;

/** Whether the environment's server understands thread.active.reorder.
    Same version-skew contract as settlement: absent reads false. */
export function readEnvironmentSupportsActiveReorder(environmentId: EnvironmentId): boolean {
  return (
    appAtomRegistry.get(environmentServerConfigsAtom).get(environmentId)?.environment.capabilities
      .threadActiveReorder === true
  );
}

/** The gate: the client door exists AND this environment's server takes the command. */
export function readActiveReorderGate(environmentId: EnvironmentId): boolean {
  return activeReorderDoor !== null && readEnvironmentSupportsActiveReorder(environmentId);
}

/** The shell's key; a missing or empty one means the thread is unplaced. */
export function readThreadActiveOrderKey(shell: EnvironmentThreadShell): string | null {
  const key = shell.activeOrderKey;
  return typeof key === "string" && key.length > 0 ? key : null;
}

/**
 * The arrangement the rail sorts by: for every sibling run that holds at
 * least one keyed thread, the keyed threads lead in key order and the
 * device's manual order places the rest. Runs without keys keep the device
 * order untouched; the same preferences object returns when nothing changes.
 */
export function mergePersistedSessionOrders(
  preferences: RoomRailPreferences,
  groups: RoomRailGroups,
  keyOf: (sessionId: string) => string | null | undefined,
): RoomRailPreferences {
  let sessionOrders: Record<string, string[]> | null = null;
  const walk = (nodes: readonly RoomRailSessionNode[], parentId: string) => {
    const siblings = nodes.map((node) => node.session.sessionId);
    if (siblings.some((id) => keyOf(id) != null)) {
      const local = preferences.sessionOrders[parentId] ?? [];
      const merged = mergeActiveOrder(local, siblings, keyOf);
      if (merged.join("\u0000") !== local.join("\u0000")) {
        sessionOrders ??= { ...preferences.sessionOrders };
        sessionOrders[parentId] = merged;
      }
    }
    for (const node of nodes) walk(node.children, node.session.sessionId);
  };
  for (const entry of groups.rooms) walk(entry.sessions, entry.room.roomId);
  return sessionOrders === null ? preferences : { ...preferences, sessionOrders };
}

export interface ActiveOrderWrite {
  readonly environmentId: EnvironmentId;
  readonly threadId: EnvironmentThreadShell["id"];
  readonly orderKey: string;
}

/**
 * Sends each key write through the reorder door, in order; one toast names
 * the first failure and the result says whether every write landed. Callers
 * check `readActiveReorderGate` first — with the door absent the hook leans
 * on the pinned door's atom only to satisfy the hook rule and never sends.
 */
export function useActiveReorderCommand(): (
  writes: ReadonlyArray<ActiveOrderWrite>,
) => Promise<boolean> {
  const send = useAtomCommand(activeReorderDoor ?? threadEnvironment.reorderPin, {
    reportFailure: false,
  });
  return useCallback(
    async (writes) => {
      if (activeReorderDoor === null) return false;
      for (const write of writes) {
        const result = await send({
          environmentId: write.environmentId,
          input: { threadId: write.threadId, orderKey: write.orderKey },
        });
        if (result._tag === "Failure") {
          const error = squashAtomCommandFailure(result);
          toastManager.add({
            type: "error",
            title: "Failed to save session order",
            description: error instanceof Error ? error.message : "An error occurred.",
          });
          return false;
        }
      }
      return true;
    },
    [send],
  );
}
